/**
 * Axial hex coordinates, pointy-top orientation (ADR-0005). Formulas follow
 * Red Blob Games "Hexagonal Grids". The implicit third cube axis is s = -q - r.
 */
export interface AxialCoord {
  readonly q: number;
  readonly r: number;
}

export interface PixelPoint {
  readonly x: number;
  readonly y: number;
}

const SQRT3 = Math.sqrt(3);
const CORNER_COUNT = 6;
const POINTY_TOP_FIRST_CORNER_DEGREES = 30;

export const HEX_DIRECTIONS: readonly AxialCoord[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export function hexKey(coord: AxialCoord): string {
  return `${coord.q},${coord.r}`;
}

export function hexEquals(a: AxialCoord, b: AxialCoord): boolean {
  return a.q === b.q && a.r === b.r;
}

export function hexAdd(a: AxialCoord, b: AxialCoord): AxialCoord {
  return { q: a.q + b.q, r: a.r + b.r };
}

export function hexNeighbors(coord: AxialCoord): AxialCoord[] {
  return HEX_DIRECTIONS.map((direction) => hexAdd(coord, direction));
}

/**
 * The hexes at exactly `radius` from `centre`, from the one due E counter-clockwise (E, NE, NW, W,
 * SW, SE for radius 1, `hexNeighbors`'s order); the centre alone for radius 0.
 */
export function hexRing(centre: AxialCoord, radius: number): AxialCoord[] {
  if (radius <= 0) {
    return [centre];
  }
  const ring: AxialCoord[] = [];
  let at: AxialCoord = { q: centre.q + radius, r: centre.r };
  for (let side = 0; side < HEX_DIRECTIONS.length; side += 1) {
    const direction = HEX_DIRECTIONS[(side + 2) % HEX_DIRECTIONS.length] ?? { q: 0, r: 0 };
    for (let step = 0; step < radius; step += 1) {
      ring.push(at);
      at = hexAdd(at, direction);
    }
  }
  return ring;
}

export function hexDistance(a: AxialCoord, b: AxialCoord): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/** Centre of a hex in pixels; `size` is the centre-to-corner radius. */
export function axialToPixel(coord: AxialCoord, size: number): PixelPoint {
  return {
    x: size * (SQRT3 * coord.q + (SQRT3 / 2) * coord.r),
    y: size * ((3 / 2) * coord.r),
  };
}

export function pixelToAxial(point: PixelPoint, size: number): AxialCoord {
  const q = ((SQRT3 / 3) * point.x - (1 / 3) * point.y) / size;
  const r = ((2 / 3) * point.y) / size;
  return hexRound(q, r);
}

/** Cube rounding: round all three axes, then fix the one with the largest error. */
export function hexRound(fractionalQ: number, fractionalR: number): AxialCoord {
  const fractionalS = -fractionalQ - fractionalR;
  let q = Math.round(fractionalQ);
  let r = Math.round(fractionalR);
  const s = Math.round(fractionalS);
  const dq = Math.abs(q - fractionalQ);
  const dr = Math.abs(r - fractionalR);
  const ds = Math.abs(s - fractionalS);
  if (dq > dr && dq > ds) {
    q = -r - s;
  } else if (dr > ds) {
    r = -q - s;
  }
  return { q, r };
}

export function hexCorners(centre: PixelPoint, size: number): PixelPoint[] {
  const corners: PixelPoint[] = [];
  for (let index = 0; index < CORNER_COUNT; index += 1) {
    const degrees = 60 * index - POINTY_TOP_FIRST_CORNER_DEGREES;
    const radians = (Math.PI / 180) * degrees;
    corners.push({
      x: centre.x + size * Math.cos(radians),
      y: centre.y + size * Math.sin(radians),
    });
  }
  return corners;
}
